import { initializeApp, type FirebaseApp, type FirebaseOptions } from 'firebase/app';
import { connectAuthEmulator, getAuth, type Auth } from 'firebase/auth';
import {
  connectFirestoreEmulator,
  initializeFirestore,
  type Firestore,
} from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, type Functions } from 'firebase/functions';
import { connectStorageEmulator, getStorage, type FirebaseStorage } from 'firebase/storage';

export const FUNCTIONS_REGION = 'europe-west1';

export interface FirebaseClients {
  app: FirebaseApp;
  auth: Auth;
  firestore: Firestore;
  storage: FirebaseStorage;
  functions: Functions;
}

export interface InitOptions {
  config: FirebaseOptions;
  useEmulators: boolean;
  emulatorHost?: string;
}

let clients: FirebaseClients | null = null;

/**
 * Initialise Firebase exactly once per page load. Subsequent calls return the
 * existing clients so HMR / re-renders don't double-register emulator hosts.
 */
export function initFirebase({
  config,
  useEmulators,
  emulatorHost = '127.0.0.1',
}: InitOptions): FirebaseClients {
  if (clients) return clients;

  const app = initializeApp(config);
  const auth = getAuth(app);
  // `ignoreUndefinedProperties: true` makes Firestore silently strip `undefined`
  // fields from writes instead of throwing. Without it, optional form fields
  // (per-module price, formateur social URLs, etc.) blow up updateDoc with
  // "Unsupported field value: undefined" when the user clears them. Must be
  // called via initializeFirestore — getFirestore returns a cached instance
  // without honouring settings.
  const firestore = initializeFirestore(app, { ignoreUndefinedProperties: true });
  const storage = getStorage(app);
  const functions = getFunctions(app, FUNCTIONS_REGION);

  if (useEmulators) {
    connectAuthEmulator(auth, `http://${emulatorHost}:9099`, { disableWarnings: true });
    connectFirestoreEmulator(firestore, emulatorHost, 8080);
    connectStorageEmulator(storage, emulatorHost, 9199);
    connectFunctionsEmulator(functions, emulatorHost, 5001);
  }

  clients = { app, auth, firestore, storage, functions };
  return clients;
}

export function getFirebaseClients(): FirebaseClients {
  if (!clients) {
    throw new Error('Firebase not initialised. Call initFirebase() in your app entry first.');
  }
  return clients;
}
